import Alpine from "alpinejs";
import gsap from "gsap";
import { EmblaApp } from "../embla/EmblaApp";
import { HOME_MV_SETTINGS } from "../settings";

export const homeMv = () => {
  Alpine.data("homeMv", () => ({
    emblaApp: null as EmblaApp | null,
    introTimeline: null as gsap.core.Timeline | null,
    onSwupOutStart: null as (() => void) | null,
    onEmblaSelect: null as (() => void) | null,
    onEmblaReInit: null as (() => void) | null,
    slideScaleTargets: [] as HTMLElement[],
    slideScaleTweens: [] as Array<gsap.core.Tween | null>,
    reduceMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,

    init() {
      const root = this.$root as HTMLElement;
      const emblaRoot = root.querySelector("[data-home-mv-embla]") as HTMLElement | null;
      if (emblaRoot) {
        this.emblaApp = new EmblaApp(emblaRoot, {
          embla: HOME_MV_SETTINGS.embla,
          setPlugins: {
            classNames: false,
            autoplay: true,
            autoScroll: false,
            fade: true,
          },
          autoplay: {
            delay: HOME_MV_SETTINGS.autoplay.delayMs,
            stopOnInteraction: HOME_MV_SETTINGS.autoplay.stopOnInteraction,
            stopOnMouseEnter: HOME_MV_SETTINGS.autoplay.stopOnMouseEnter,
            playOnInit: HOME_MV_SETTINGS.autoplay.playOnInit,
          },
          autoScroll: {},
        });
        this.setupSlideScaleAnimation();
      }

      this.playIntroAnimation();
      this.bindPageLeaveCleanup();
    },

    playIntroAnimation() {
      const root = this.$root as HTMLElement;
      const background = root.querySelector("[data-home-mv-embla]") as HTMLElement | null;
      const baseOverlay = root.querySelector("[data-home-mv-base-overlay]") as HTMLElement | null;
      const shade = root.querySelector("[data-home-mv-shade]") as HTMLElement | null;
      const title = root.querySelector("[data-home-mv-title]") as HTMLElement | null;
      const titleChars = title
        ? Array.from(title.querySelectorAll("[data-home-mv-title-char]")) as HTMLElement[]
        : [];
      const copy = root.querySelector("[data-home-mv-copy]") as HTMLElement | null;
      const header = document.getElementById("home-header");
      const intro = HOME_MV_SETTINGS.animation;

      if (background) background.removeAttribute("x-cloak");
      if (baseOverlay) baseOverlay.removeAttribute("x-cloak");
      if (shade) shade.removeAttribute("x-cloak");
      if (title) title.removeAttribute("x-cloak");
      if (copy) copy.removeAttribute("x-cloak");
      if (header) header.removeAttribute("x-cloak");

      if (this.reduceMotion) {
        if (background) gsap.set(background, { autoAlpha: 1 });
        if (baseOverlay) gsap.set(baseOverlay, { autoAlpha: 1 });
        if (shade) gsap.set(shade, { autoAlpha: 1 });
        if (title) gsap.set(title, { autoAlpha: 1 });
        if (titleChars.length > 0) gsap.set(titleChars, { autoAlpha: 1 });
        if (copy) gsap.set(copy, { autoAlpha: 1 });
        if (header) {
          gsap.set(header, { autoAlpha: 1 });
          header.style.pointerEvents = "auto";
        }
        return;
      }

      this.introTimeline?.kill();
      const tl = gsap.timeline();

      const backgroundTargets: HTMLElement[] = [];
      if (background) backgroundTargets.push(background);
      if (baseOverlay) backgroundTargets.push(baseOverlay);
      if (shade) backgroundTargets.push(shade);

      if (backgroundTargets.length > 0) {
        gsap.set(backgroundTargets, { autoAlpha: 0 });
        tl.to(backgroundTargets, {
          autoAlpha: 1,
          duration: intro.backgroundFadeDurationSec,
          ease: intro.backgroundFadeEase,
        }, intro.backgroundFadeStartSec);
      }

      if (title) {
        if (titleChars.length > 0) {
          gsap.set(title, { autoAlpha: 1 });
          gsap.set(titleChars, { autoAlpha: 0 });
          tl.to(titleChars, {
            autoAlpha: 1,
            duration: intro.titleCharFadeDurationSec,
            ease: intro.titleFadeEase,
            stagger: {
              each: intro.titleCharStaggerSec,
              from: "end",
            },
          }, intro.titleFadeStartSec);
        } else {
          gsap.set(title, { autoAlpha: 0 });
          tl.to(title, {
            autoAlpha: 1,
            duration: intro.titleFadeDurationSec,
            ease: intro.titleFadeEase,
          }, intro.titleFadeStartSec);
        }
      }

      if (copy) {
        gsap.set(copy, { autoAlpha: 0 });
        tl.to(copy, {
          autoAlpha: 1,
          duration: intro.copyFadeDurationSec,
          ease: intro.copyFadeEase,
        }, intro.copyFadeStartSec);
      }

      if (header) {
        gsap.set(header, { autoAlpha: 0 });
        tl.to(header, {
          autoAlpha: 1,
          duration: intro.headerFadeDurationSec,
          ease: intro.headerFadeEase,
          onStart: () => {
            header.style.pointerEvents = "auto";
          },
        }, intro.headerFadeStartSec);
      }

      this.introTimeline = tl;
    },

    setupSlideScaleAnimation() {
      const emblaApi = this.emblaApp?.emblaApi;
      if (!emblaApi) return;

      if (this.onEmblaSelect) emblaApi.off("select", this.onEmblaSelect);
      if (this.onEmblaReInit) emblaApi.off("reInit", this.onEmblaReInit);
      this.slideScaleTweens.forEach((tween) => tween?.kill());

      this.slideScaleTargets = emblaApi.slideNodes().map((slide) => {
        const image = slide.querySelector("img");
        return (image as HTMLElement | null) ?? (slide as HTMLElement);
      });
      this.slideScaleTweens = this.slideScaleTargets.map(() => null);

      gsap.set(this.slideScaleTargets, {
        scale: HOME_MV_SETTINGS.animation.slideScaleStart,
        transformOrigin: "center center",
      });

      const handleSelect = () => {
        const selectedIndex = emblaApi.selectedScrollSnap();
        const previousIndex = emblaApi.previousScrollSnap();

        if (previousIndex !== selectedIndex) {
          this.keepPreviousSlideScaling(previousIndex);
        }
        this.animateSelectedSlideScale(selectedIndex);
        this.resetIdleSlideScale(selectedIndex, previousIndex);
      };

      const handleReInit = () => {
        this.setupSlideScaleAnimation();
      };

      this.onEmblaSelect = handleSelect;
      this.onEmblaReInit = handleReInit;
      emblaApi.on("select", handleSelect);
      emblaApi.on("reInit", handleReInit);

      this.animateSelectedSlideScale(emblaApi.selectedScrollSnap());
    },

    animateSelectedSlideScale(index: number) {
      const target = this.slideScaleTargets[index];
      if (!target) return;

      this.slideScaleTweens[index]?.kill();
      gsap.set(target, { scale: HOME_MV_SETTINGS.animation.slideScaleStart });
      this.slideScaleTweens[index] = gsap.to(target, {
        scale: HOME_MV_SETTINGS.animation.slideScaleEnd,
        duration: HOME_MV_SETTINGS.animation.slideScaleDurationSec,
        ease: HOME_MV_SETTINGS.animation.slideScaleEase,
      });
    },

    keepPreviousSlideScaling(index: number) {
      const target = this.slideScaleTargets[index];
      if (!target) return;

      const activeTween = this.slideScaleTweens[index];
      if (activeTween && activeTween.isActive()) return;

      const currentScale = Number(gsap.getProperty(target, "scale"));
      const targetScale = HOME_MV_SETTINGS.animation.slideScaleEnd;
      if (currentScale <= targetScale) return;

      this.slideScaleTweens[index]?.kill();
      this.slideScaleTweens[index] = gsap.to(target, {
        scale: targetScale,
        duration: HOME_MV_SETTINGS.animation.previousSlideExitScaleDurationSec,
        ease: HOME_MV_SETTINGS.animation.slideScaleEase,
      });
    },

    resetIdleSlideScale(selectedIndex: number, previousIndex: number) {
      this.slideScaleTargets.forEach((target, index) => {
        if (index === selectedIndex || index === previousIndex) return;
        this.slideScaleTweens[index]?.kill();
        this.slideScaleTweens[index] = null;
        gsap.set(target, { scale: HOME_MV_SETTINGS.animation.slideScaleStart });
      });
    },

    bindPageLeaveCleanup() {
      const handleSwupOutStart = () => {
        this.destroy();
      };

      this.onSwupOutStart = handleSwupOutStart;
      document.addEventListener("swup:animation:out:start", handleSwupOutStart, {
        once: true,
      });
    },

    destroy() {
      const emblaApi = this.emblaApp?.emblaApi;
      if (emblaApi) {
        if (this.onEmblaSelect) emblaApi.off("select", this.onEmblaSelect);
        if (this.onEmblaReInit) emblaApi.off("reInit", this.onEmblaReInit);
        emblaApi.destroy();
      }
      this.emblaApp = null;
      this.slideScaleTweens.forEach((tween) => tween?.kill());
      this.slideScaleTweens = [];
      this.slideScaleTargets = [];
      this.onEmblaSelect = null;
      this.onEmblaReInit = null;
      this.introTimeline?.kill();
      this.introTimeline = null;

      if (this.onSwupOutStart) {
        document.removeEventListener(
          "swup:animation:out:start",
          this.onSwupOutStart
        );
        this.onSwupOutStart = null;
      }
    },
  }));
};
